import React from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  IconButton,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import {
  buildConditionalPendingSteps,
  type ConditionalPendingRequestData,
  type TrackerStepState,
} from "./conditionalPendingUtils";
import {
  requestDialogFooterSx,
  requestDialogHeaderSx,
  requestDialogPaperSx,
  requestDialogShellSx,
} from "./requestDialogLayout";
import { FN } from "./facilityNetworkTheme";

type Props = {
  open: boolean;
  request: ConditionalPendingRequestData | null;
  submitting?: boolean;
  onClose: () => void;
  onCancel: () => void;
};

const STEP_STYLES: Record<
  TrackerStepState,
  { circleBg: string; circleBorder: string; circleColor: string; title: string; detail: string }
> = {
  done: {
    circleBg: "#e5f5f8",
    circleBorder: "#bfe5ec",
    circleColor: "#033843",
    title: "#0a829d",
    detail: FN.figmaAccent,
  },
  active: {
    circleBg: "#fff4e5",
    circleBorder: "#fabc89",
    circleColor: "#8b4409",
    title: "#8b4409",
    detail: FN.figmaAccent,
  },
  pending: {
    circleBg: "#f3f2f1",
    circleBorder: "#c8c6c4",
    circleColor: "#737373",
    title: "#737373",
    detail: "#d4d4d4",
  },
};

function StepConnector({ active }: { active: boolean }) {
  return (
    <Box
      sx={{
        width: 48,
        height: 25,
        display: "flex",
        justifyContent: "center",
        alignItems: "stretch",
        py: "2px",
        boxSizing: "border-box",
      }}
    >
      {/* Fixed width — do not use flex:1 (row flex stretches it into a bar) */}
      <Box
        sx={{
          width: 2,
          alignSelf: "stretch",
          flexShrink: 0,
          bgcolor: active ? "#bfe5ec" : "#e5e5e5",
          borderRadius: "1px",
        }}
      />
    </Box>
  );
}

/** Figma 9227:111011 — Conditional pending request stepper for C (P_new) */
export default function FacilityNetworkConditionalPendingDialog({
  open,
  request,
  submitting = false,
  onClose,
  onCancel,
}: Props) {
  if (!request) return null;

  const steps = buildConditionalPendingSteps(request);
  const expires = request.expiresInDays;

  return (
    <Dialog
      open={open}
      onClose={submitting ? undefined : onClose}
      maxWidth={false}
      PaperProps={{ sx: { ...requestDialogPaperSx, maxWidth: 720, width: "100%" } }}
    >
      <Box sx={requestDialogShellSx}>
        <Box sx={requestDialogHeaderSx}>
          <Typography
            sx={{
              fontFamily: FN.fontFamily,
              fontSize: 18,
              fontWeight: 700,
              color: FN.figmaNeutral,
              flex: 1,
            }}
          >
            Conditional pending request
          </Typography>
          <IconButton size="small" onClick={onClose} disabled={submitting} aria-label="Close">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        <Box sx={{ px: 3, py: 2, display: "flex", flexDirection: "column", gap: 1 }}>
          <Typography
            sx={{
              fontFamily: FN.fontFamily,
              fontSize: 14,
              color: FN.figmaAccent,
              lineHeight: "20px",
              pb: 2,
              borderBottom: "1px solid #e5e5e5",
            }}
          >
            {`Awaiting approval from ${request.childFacilityName} and their current parent`}
          </Typography>

          <Box sx={{ display: "flex", flexDirection: "column", py: 1 }}>
            {steps.map((step, index) => {
              const style = STEP_STYLES[step.state];
              return (
                <Box key={step.number}>
                  <Box
                    sx={{
                      display: "flex",
                      gap: "10px",
                      alignItems: "center",
                      px: 1,
                      py: 0.5,
                    }}
                  >
                    <Box
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: "24px",
                        bgcolor: style.circleBg,
                        border: `1px solid ${style.circleBorder}`,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                    >
                      <Typography
                        sx={{
                          fontFamily: FN.fontFamily,
                          fontSize: 14,
                          fontWeight: 600,
                          color: style.circleColor,
                          lineHeight: "20px",
                        }}
                      >
                        {step.number}
                      </Typography>
                    </Box>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: "2px", minWidth: 0, flex: 1 }}>
                      <Typography
                        sx={{
                          fontFamily: FN.fontFamily,
                          fontSize: 14,
                          fontWeight: 600,
                          color: style.title,
                          lineHeight: "20px",
                          whiteSpace: "normal",
                          wordBreak: "break-word",
                        }}
                      >
                        {step.title}
                      </Typography>
                      <Typography
                        sx={{
                          fontFamily: FN.fontFamily,
                          fontSize: 14,
                          fontWeight: 400,
                          color: style.detail,
                          lineHeight: "20px",
                        }}
                      >
                        {step.detail}
                      </Typography>
                    </Box>
                  </Box>
                  {index < steps.length - 1 ? (
                    <StepConnector active={step.state === "done"} />
                  ) : null}
                </Box>
              );
            })}
          </Box>
        </Box>

        <Box
          sx={{
            ...requestDialogFooterSx,
            justifyContent: "space-between",
            borderTop: "1px solid #e5e5e5",
            pt: 2,
          }}
        >
          <Typography
            sx={{
              fontFamily: FN.fontFamily,
              fontSize: 14,
              color: FN.figmaAccent,
              lineHeight: "20px",
            }}
          >
            Request expires in{" "}
            <Box component="span" sx={{ fontWeight: 700 }}>
              {expires == null ? "—" : `${expires} day${expires === 1 ? "" : "s"}`}
            </Box>{" "}
            if no action is taken
          </Typography>
          <Button
            variant="outlined"
            disabled={submitting}
            onClick={onCancel}
            sx={{
              fontFamily: FN.fontFamily,
              textTransform: "none",
              color: "#a4262c",
              borderColor: "#e7a3a7",
              bgcolor: "#fff",
              "&:hover": { borderColor: "#a4262c", bgcolor: "#fde7e9" },
            }}
          >
            {submitting ? <CircularProgress size={18} color="inherit" /> : "Cancel request"}
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
}
